import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
} from '@components/inspector-tabs';
import Visibility from '@components/visibility';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import Label from '@components/block-label';
import { DEFAULT_CSS_CLASS, BERG_STORE } from '@constants';
import {
  setUniqueIdAttribute,
  columnSizeClassOutput,
  flexClasses,
} from '@helpers/utils.js';
import BackgroundTypes from '@components/background-types';
import BackgroundTypesSave from '@components/background-types/save';
import FlexSettings from '@components/flex-settings';
import {
  FocusableIcon,
  FocusableIconSwitcher,
} from '@components/focusable-block-icon';
import { title } from './block.json';
import columnIcon from './icon';
import classnames from 'classnames';

import './editor.scss';

const {
  i18n: { __ },
  blockEditor: { useBlockProps, useInnerBlocksProps },
  element: { useEffect },
  components: { PanelBody, RangeControl },
  data: { select },
} = wp;

const Edit = (props) => {
  const { attributes, setAttributes, name } = props;
  const {
    label,
    blockId,
    blockVisibility,
    blockClassNames,
    backgroundType,
    backgroundGradientOptions,
    backgroundColorOptions,
    backgroundImageOptions,
    backgroundImageAndColorOptions,
    imageAsBackgroundOptions,
    backgroundVideoOptions,
    imageGradientBackgroundOptions,
    columnSize,
    flexSettings,
    enableFocusableIcon,
    printBackgroundStyles,
  } = attributes;
  const columnSizeClassOptions = select(BERG_STORE).getBlockAttributeOptions(
    name,
    'columnSize',
  );

  const displayColumnSize = () => {
    const columnSizeElm = [];
    for (const [key, value] of Object.entries(columnSize)) {
      const label = `${key} Column Count`;
      columnSizeElm.push(
        <RangeControl
          key={key}
          label={label}
          value={value}
          initialPosition={columnSizeClassOptions[key]}
          min={0}
          max={12}
          onChange={(selectedColumnSize) => {
            setAttributes({
              columnSize: {
                ...columnSize,
                [key]: selectedColumnSize,
              },
            });
          }}
        />,
      );
    }

    return columnSizeElm;
  };

  useEffect(() => {
    setUniqueIdAttribute(name, 'blockId', attributes, setAttributes);
  }, [name, attributes, setAttributes]);

  const defaultCssLibrary = select(BERG_STORE).getDefaultCssLibrary();

  const defaultBlockCssClass = DEFAULT_CSS_CLASS.COLUMN;
  const defaultBlockDynamicCssClass = `${defaultBlockCssClass}-${blockId}`;
  const classNames = getBlockClassNames(blockClassNames);

  const columnSizeClass = columnSizeClassOutput(defaultCssLibrary, columnSize);
  const flexSettingsClass = flexClasses(flexSettings);

  const blockProps = useBlockProps({
    className: classnames({
      [`${defaultBlockCssClass}`]: true,
      [`${defaultBlockDynamicCssClass}`]: defaultBlockDynamicCssClass,
      [`${classNames}`]: classNames,
      [`${columnSizeClass}`]: columnSizeClass,
      [`${flexSettingsClass}`]: flexSettingsClass,
      disable: !blockVisibility,
    }),
  });

  const { children, ...innerBlocksProps } = useInnerBlocksProps(blockProps, {});

  return (
    <>
      <InspectorTabs>
        <InspectorControlsGeneral>
          <Label {...{ label, setAttributes }} />
          <Visibility {...{ blockVisibility, setAttributes }} />
          <PanelBody title={__('Column Size', 'b3rg')} initialOpen={false}>
            {displayColumnSize()}
          </PanelBody>
          <FlexSettings {...{ flexSettings, setAttributes, name }} />
          <BackgroundTypes
            {...{
              blockId,
              setAttributes,
              backgroundType,
              backgroundGradientOptions,
              backgroundColorOptions,
              backgroundImageOptions,
              backgroundImageAndColorOptions,
              imageAsBackgroundOptions,
              backgroundVideoOptions,
              imageGradientBackgroundOptions,
              blockClassNames,
              printBackgroundStyles,
            }}
          />
        </InspectorControlsGeneral>
        <InspectorControlsAppearance>
          <ClassDropdown
            {...{
              blockClassNames,
              setAttributes,
              initialOpen: true,
              blockClassPrefix: DEFAULT_CSS_CLASS.COLUMN,
            }}
          />
          <FocusableIconSwitcher
            {...{
              enableFocusableIcon,
              setAttributes,
            }}
          />
        </InspectorControlsAppearance>
      </InspectorTabs>
      <div {...innerBlocksProps}>
        {enableFocusableIcon && (
          <FocusableIcon {...{ title, icon: columnIcon }} />
        )}
        <BackgroundTypesSave
          {...{
            blockId,
            defaultBlockCssClass,
            backgroundType,
            backgroundGradientOptions,
            backgroundColorOptions,
            backgroundImageOptions,
            backgroundImageAndColorOptions,
            imageAsBackgroundOptions,
            backgroundVideoOptions,
            imageGradientBackgroundOptions,
          }}
        />
        {children}
      </div>
    </>
  );
};

export default Edit;
